import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    descriptions = "descriptions",
    btnBrand = "btnBrand",
    btnCategories = "btnCategories",
    btnContact = "btnContact",
    buttonLink = "buttonLink",
    btnHomepage = "btnHomepage",
    buttonBackgroundColor = "buttonBackgroundColor",
    buttonLabelColor = "buttonLabelColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.descriptions]: string;
        [fields.btnBrand]: string;
        [fields.btnCategories]: string;
        [fields.btnContact]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.btnHomepage]: string;
        [fields.buttonBackgroundColor]: string;
        [fields.buttonLabelColor]: string;
    };
}

export interface Imgstyle {
    parent?: InjectableCss;
    titleStyle?: InjectableCss;
    descriptionStyle?: InjectableCss;
    buttonStyle?: InjectableCss;
    btnWrapper?: InjectableCss;
    link?: InjectableCss;
}

export const pageNotFoundStyle: Imgstyle = {
    parent: {
        css: css`
            border: 4px solid grayShade;
            background: rgba(38, 107, 64, 0.04);
            padding: 30px 20px;
            max-width: 750px;
            width: 100%;
            margin: 94px auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 88%;
                        margin: 54px auto;
                    `,
                    css`
                        margin: 54px auto;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    titleStyle: {
        css: css`
            margin-bottom: 28px !important;
            font-size: ${TYPOGRAPHY.size.largest} !important;
            margin-bottom: 1rem;
            display: block;
            margin: auto;
            text-align: center;
            font-weight: ${TYPOGRAPHY.weight.black} !important;
            color: ${getProp("theme.typography.p.color")} !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        font-size: 18px !important;
                        margin-bottom: 40px !important;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    descriptionStyle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.base} !important;
            font-weight: ${TYPOGRAPHY.weight.base} !important;
            margin-bottom: 1rem;
            display: block;
            text-align: center;
            color: ${getProp("theme.typography.p.color")} !important;
        `,
    },

    btnWrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            gap: 20px;
            margin-bottom: 20px;
        `,
    },

    link: {
        css: css`
            color: white;
        `,
    },
};

const styles = pageNotFoundStyle;

export const CENPagenotFound: FC<OwnProps> = ({ fields }) => {
    const buttonStyle = {
        css: css`
            flex-basis: 48%;
            padding: 18px;
            border-radius: 0 !important;
            background: ${fields.buttonBackgroundColor} !important;
            border: 2px solid ${fields.buttonBackgroundColor} !important;
            span {
                padding: 0px 21px;
                font-size: 15px !important;
                background: ${fields.buttonBackgroundColor} !important;
                font-weight: ${TYPOGRAPHY.weight.black} !important;
                color: ${fields.buttonLabelColor} !important;
            }
        `,
    };
    const { url } = useGetLink(fields.buttonLink);
    const onClick = () => {
        if (!url) {
            return;
        }
        window.location.href = url;
    };

    const handleclick = (url: any) => {
        location.href = `/${url}`;
    };
    return (
        <StyledWrapper {...styles.parent}>
            <StyledWrapper {...styles.titleStyle}>{fields.title}</StyledWrapper>
            <StyledWrapper {...styles.descriptionStyle}>{fields.description}</StyledWrapper>
            <StyledWrapper {...styles.descriptionStyle}>{fields.descriptions}</StyledWrapper>
            <StyledWrapper {...styles.btnWrapper}>
                <Button
                    onClick={() => {
                        handleclick("brands");
                    }}
                    {...buttonStyle}
                >
                    {fields.btnBrand}
                </Button>
                <Button
                    onClick={() => {
                        handleclick("categories");
                    }}
                    {...buttonStyle}
                >
                    {fields.btnCategories}
                </Button>
            </StyledWrapper>
            <StyledWrapper {...styles.btnWrapper}>
                <Button onClick={onClick} {...buttonStyle}>
                    {fields.btnContact}
                </Button>
                <Button
                    onClick={() => {
                        handleclick(" ");
                    }}
                    {...buttonStyle}
                >
                    {fields.btnHomepage}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CENPagenotFound,
    definition: {
        group: "Basic",
        displayName: "PageNOTFound",
        fieldDefinitions: [
            {
                name: fields.title,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },

            {
                name: fields.description,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.descriptions,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },

            {
                fieldType: "Translatable",
                name: fields.btnBrand,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },

            {
                fieldType: "Translatable",
                name: fields.btnCategories,
                editorTemplate: "TextField",
                displayName: "Button Label1",
                defaultValue: "",
            },

            {
                fieldType: "Translatable",
                name: fields.btnContact,
                editorTemplate: "TextField",
                displayName: "Button Label2",
                defaultValue: "",
            },

            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },

            {
                fieldType: "Translatable",
                name: fields.btnHomepage,
                editorTemplate: "TextField",
                displayName: "Button Label3",
                defaultValue: "",
            },
            {
                name: fields.buttonBackgroundColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "#8f0446",
                fieldType: "General",
                displayName: "Button Background Color",
            },
            {
                name: fields.buttonLabelColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "white",
                fieldType: "General",
                displayName: "Button Label Color",
            },
        ],
    },
};

export default widgetModule;
